import {
  createBotPilot,
  type BotMoveRequest,
  type BotPilot,
  type BotReply,
  type BotRequest,
  type BotStart,
  type BotStartRequest,
  type BotWorkerInbound,
  type BotWorkerOutbound,
} from '../../sim/bot/index.ts';
import type { MapDocument } from '../../sim/map/types.ts';
import type { Vec2 } from '../../sim/types.ts';

/**
 * Asks the bot for a start or a click without blocking the frame. The search runs in a module
 * worker; if the worker cannot start or throws, the same pilot runs on the main thread instead
 * (one hitch per turn, but the race keeps going).
 */
export type BotClient = {
  start: (request: Omit<BotStartRequest, 'kind'>) => Promise<BotStart | null>;
  move: (request: Omit<BotMoveRequest, 'kind'>) => Promise<Vec2>;
  destroy: () => void;
};

type Pending = { request: BotRequest; resolve: (reply: BotReply) => void; reject: (error: Error) => void };

export function createBotClient(
  map: MapDocument,
  mapId: string,
  /** This race's Collisions choice. The worker has its own copy of the host flag. */
  boatCollisions: boolean,
): BotClient {
  let inline: BotPilot | null = null;
  let worker: Worker | null = null;
  let nextId = 1;
  let destroyed = false;
  const pending = new Map<number, Pending>();

  const inlinePilot = (): BotPilot => {
    inline ??= createBotPilot(map, mapId);
    return inline;
  };

  const answerInline = (entry: Pending): void => {
    setTimeout(() => {
      if (destroyed) {
        return;
      }

      try {
        entry.resolve(inlinePilot().answer(entry.request));
      } catch (error) {
        entry.reject(error instanceof Error ? error : new Error(String(error)));
      }
    }, 0);
  };

  const dropWorker = (): void => {
    worker?.terminate();
    worker = null;

    for (const entry of pending.values()) {
      answerInline(entry);
    }

    pending.clear();
  };

  try {
    worker = new Worker(new URL('./botWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<BotWorkerOutbound>) => {
      const message = event.data;
      const entry = pending.get(message.id);

      if (!entry) {
        return;
      }

      pending.delete(message.id);

      if (message.type === 'reply') {
        entry.resolve(message.reply);
      } else {
        console.warn(`Bot worker: ${message.message}; answering on the main thread.`);
        answerInline(entry);
      }
    };
    worker.onerror = (event) => {
      event.preventDefault();
      console.warn('Bot worker failed; answering on the main thread.', event.message);
      dropWorker();
    };
    worker.postMessage({ type: 'init', map, mapId, boatCollisions } satisfies BotWorkerInbound);
  } catch {
    worker = null;
  }

  const ask = (request: BotRequest): Promise<BotReply> =>
    new Promise((resolve, reject) => {
      const entry = { request, resolve, reject };

      if (!worker) {
        answerInline(entry);
        return;
      }

      const id = nextId;
      nextId += 1;
      pending.set(id, entry);
      worker.postMessage({ type: 'request', id, request } satisfies BotWorkerInbound);
    });

  return {
    async start(request) {
      const reply = await ask({ kind: 'start', ...request });
      return reply.kind === 'start' ? reply.start : null;
    },
    async move(request) {
      const reply = await ask({ kind: 'move', ...request });

      if (reply.kind !== 'move') {
        throw new Error('bot answered a move with a start');
      }

      return reply.destination;
    },
    destroy() {
      destroyed = true;
      worker?.terminate();
      worker = null;
      pending.clear();
    },
  };
}
